import {createApiCall} from '../utils';
import {SERVICE_NAMES} from '../constants';
import type {ApiCallOptions} from '../types';
import type {
    BrandResource,
    BrandsListResponse,
    BrandsListParams,
    CreateBrandRequest,
    UpdateBrandRequest,
    UpdateLogoRequest,
    CustomEmailContentResource,
    UpdateCustomEmailContentRequest,
    TemplateConfigurationResource,
    UpdateTemplateConfigurationRequest,
    TemplateKey,
    Locale
} from './api.d';

const createBrandApiCall = <T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string
) => createApiCall<T>(SERVICE_NAMES.BRAND, method, endpoint);

export const getDefaultBrand = (accountId: string, options: ApiCallOptions = {}) =>
    createBrandApiCall<BrandResource>('GET', `accounts/${accountId}/default-brand`)({...options});

export const getBrands = (accountId: string, params?: BrandsListParams, options: ApiCallOptions = {}) =>
    createBrandApiCall<BrandsListResponse>('GET', `accounts/${accountId}/brands`)({...options, params});

export const getBrand = (accountId: string, brandId: string, options: ApiCallOptions = {}) =>
    createBrandApiCall<BrandResource>('GET', `accounts/${accountId}/brands/${brandId}`)({...options});

export const createBrand = (accountId: string, data: CreateBrandRequest, options: ApiCallOptions = {}) => {
    const formData = new FormData();

    formData.append('name', data.name);
    formData.append('signing_logo', data.signing_logo);
    formData.append('email_logo', data.email_logo);
    formData.append('font_color', data.font_color);
    formData.append('link_color', data.link_color);
    formData.append('button_background_color', data.button_background_color);
    formData.append('button_text_color', data.button_text_color);
    if (data.url_recipient_session_expired) {
        formData.append('url_recipient_session_expired', data.url_recipient_session_expired);
    }
    if (data.url_user_session_expired) {
        formData.append('url_user_session_expired', data.url_user_session_expired);
    }
    if (data.url_logout) {
        formData.append('url_logout', data.url_logout);
    }

    if (data.sender_email) {
        formData.append('sender_email', data.sender_email);
    }
    if (data.sender_name) {
        formData.append('sender_name', data.sender_name);
    }
    if (data.custom_owner_name) {
        formData.append('custom_owner_name', data.custom_owner_name);
    }
    if (data.custom_owner_email) {
        formData.append('custom_owner_email', data.custom_owner_email);
    }

    return createBrandApiCall<BrandResource>('POST', `accounts/${accountId}/brands`)({
        ...options,
        data: formData
    });
};

export const updateBrand = (accountId: string, brandId: string, data: UpdateBrandRequest, options: ApiCallOptions = {}) =>
    createBrandApiCall<BrandResource>('PUT', `accounts/${accountId}/brands/${brandId}`)({
        ...options,
        data
    });

export const updateLogo = (accountId: string, brandId: string, data: UpdateLogoRequest, options: ApiCallOptions = {}) => {
    const formData = new FormData();

    if (data.signing_logo) {
        formData.append('signing_logo', data.signing_logo);
    }
    if (data.email_logo) {
        formData.append('email_logo', data.email_logo);
    }

    return createBrandApiCall<BrandResource>('POST', `accounts/${accountId}/brands/${brandId}/update-logo`)({
        ...options,
        data: formData
    });
};

export const setDefaultBrand = (accountId: string, brandId: string, options: ApiCallOptions = {}) =>
    createBrandApiCall<BrandResource>('PATCH', `accounts/${accountId}/brands/${brandId}/set-default`)({...options});

export const deleteBrand = (accountId: string, brandId: string, options: ApiCallOptions = {}) =>
    createBrandApiCall<void>('DELETE', `accounts/${accountId}/brands/${brandId}`)({...options});

export const getCustomEmailContents = (
    accountId: string,
    brandId: string,
    templateKey: TemplateKey,
    options: ApiCallOptions = {}
) =>
    createBrandApiCall<CustomEmailContentResource[]>(
        'GET',
        `accounts/${accountId}/brands/${brandId}/custom-email-contents/${templateKey}`
    )({...options});

export const getCustomEmailContent = (
    accountId: string,
    brandId: string,
    templateKey: TemplateKey,
    locale: Locale,
    options: ApiCallOptions = {}
) =>
    createBrandApiCall<CustomEmailContentResource>(
        'GET',
        `accounts/${accountId}/brands/${brandId}/custom-email-contents/${templateKey}/${locale}`
    )({...options});

export const updateCustomEmailContent = (
    accountId: string,
    brandId: string,
    templateKey: TemplateKey,
    locale: Locale,
    data: UpdateCustomEmailContentRequest,
    options: ApiCallOptions = {}
) =>
    createBrandApiCall<CustomEmailContentResource>(
        'PUT',
        `accounts/${accountId}/brands/${brandId}/custom-email-contents/${templateKey}/${locale}`
    )({...options, data});

export const deleteCustomEmailContent = (
    accountId: string,
    brandId: string,
    templateKey: TemplateKey,
    locale: Locale,
    options: ApiCallOptions = {}
) =>
    createBrandApiCall<void>(
        'DELETE',
        `accounts/${accountId}/brands/${brandId}/custom-email-contents/${templateKey}/${locale}`
    )({...options});

export const getTemplateConfigurations = (
    accountId: string,
    brandId: string,
    options: ApiCallOptions = {}
) =>
    createBrandApiCall<TemplateConfigurationResource[]>(
        'GET',
        `accounts/${accountId}/brands/${brandId}/template-configurations`
    )({...options});

export const getTemplateConfiguration = (
    accountId: string,
    brandId: string,
    templateKey: TemplateKey,
    options: ApiCallOptions = {}
) =>
    createBrandApiCall<TemplateConfigurationResource>(
        'GET',
        `accounts/${accountId}/brands/${brandId}/template-configurations/${templateKey}`
    )({...options});

export const updateTemplateConfiguration = (
    accountId: string,
    brandId: string,
    templateKey: TemplateKey,
    data: UpdateTemplateConfigurationRequest,
    options: ApiCallOptions = {}
) =>
    createBrandApiCall<TemplateConfigurationResource>(
        'PUT',
        `accounts/${accountId}/brands/${brandId}/template-configurations/${templateKey}`
    )({...options, data});

export const deleteTemplateConfiguration = (
    accountId: string,
    brandId: string,
    templateKey: TemplateKey,
    options: ApiCallOptions = {}
) =>
    createBrandApiCall<void>(
        'DELETE',
        `accounts/${accountId}/brands/${brandId}/template-configurations/${templateKey}`
    )({...options});

export const brandApi = {
    getDefaultBrand,
    getBrands,
    getBrand,
    createBrand,
    updateBrand,
    updateLogo,
    setDefaultBrand,
    deleteBrand,
    getCustomEmailContents,
    getCustomEmailContent,
    updateCustomEmailContent,
    deleteCustomEmailContent,
    getTemplateConfigurations,
    getTemplateConfiguration,
    updateTemplateConfiguration,
    deleteTemplateConfiguration
};
